import { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);

const Spark = ({ data, up, k }) => {
  if (!Array.isArray(data) || data.length < 2) return null;
  const min = Math.min(...data), max = Math.max(...data), span = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 64},${18 - ((v - min) / span) * 16}`).join(" ");
  const color = up ? "#34d399" : "#fb7185";
  return (
    <svg viewBox="0 0 64 20" className="h-5 w-16" aria-label="30-day trend" data-testid={`ticker-spark-${k}`}>
      <polyline points={`0,20 ${pts} 64,20`} fill={color} fillOpacity="0.12" stroke="none" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
};

const Item = ({ it }) => {
  const pct = it?.change_pct;
  const up = (pct ?? 0) >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className="flex items-center gap-3 px-6">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300">{it.name}</span>
      <span className="font-display text-sm font-bold text-white" data-testid={`ticker-price-${it.key}`}>${fmt(it.price_usd_t)}<span className="ml-1 text-[11px] font-medium text-slate-400">/t</span></span>
      <Spark data={it.spark} up={Array.isArray(it.spark) && it.spark.length > 1 && it.spark[it.spark.length - 1] >= it.spark[0]} k={it.key} />
      {typeof pct === "number" && (
        <span className={`flex items-center gap-1 font-mono text-[11px] ${up ? "text-emerald-400" : "text-rose-400"}`}>
          <Icon className="h-3 w-3" />{up ? "+" : ""}{pct}%
        </span>
      )}
      <span className="text-gold-light">◆</span>
    </span>
  );
};

export const PriceTicker = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!API) return undefined;
    let alive = true;
    const load = () => axios.get(`${API}/api/prices`).then((r) => { if (alive) setData(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 15 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const items = Array.isArray(data?.items) ? data.items : [];
  if (!items.length) return null;
  const asOf = items[0]?.as_of ? new Date(items[0].as_of).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) : "";
  const meta = <span className="px-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{data?.source} · {asOf} UTC · {data?.official ? "daily official close" : "indicative"}{Array.isArray(items[0]?.spark) ? " · 30-day trend" : ""}</span>;
  const row = (hidden) => (
    <div className="flex shrink-0 items-center" aria-hidden={hidden}>
      {[0, 1].map((k) => <span key={k} className="flex items-center">{items.map((it) => <Item key={it.key} it={it} />)}{meta}</span>)}
    </div>
  );

  return (
    <div className="flex h-9 items-center overflow-hidden bg-navy-deep" data-testid="price-ticker">
      <span className="z-10 flex h-full shrink-0 items-center gap-2 bg-navy-deep pl-5 pr-4 font-mono text-[10px] uppercase tracking-[0.25em] text-gold-light sm:pl-8">
        <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /><span className="relative h-2 w-2 rounded-full bg-emerald-400" /></span>
        Market
      </span>
      <div className="ticker flex w-max">{row(false)}{row(true)}</div>
    </div>
  );
};
